import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useContext, useMemo, useState } from 'react';

import { SelectInput } from '@/components/common/select_input/SelectInput';
import { Spinner } from '@/components/common/Spinner';
import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useAdminApplicationCycle from '@/hooks/Admin/useAdminApplicationCycle';
import { CopyApplicationCyclesResponse } from '@/models/Admin/CopyApplicationCycles/CopyApplicationCyclesResponse';
import { ProgramNames } from '@/models/Program';
import { formatErrorMessage } from '@/utils/errorHandlerUtil';

type Props = {
  onClose: () => void;
};

const Admin_CopyApplicationCycleModal: React.FC<Props> = ({ onClose }) => {
  const theme = useTheme();
  const { setAlert } = useContext(SnackbarContext);

  const { applicationCycles, refetchApplicationCycles } = useContext(ApplicationCycleContext);
  const { copyApplicationCycles } = useAdminApplicationCycle();

  const [saving, setSaving] = useState(false);
  const [copyFromYear, setCopyFromYear] = useState<number | null>(null);
  const [copyToYear, setCopyToYear] = useState<number | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const nonTreeLineCycles = useMemo(
    () => applicationCycles.filter((ac) => ac.programId !== ProgramNames.TreeLineUSA),
    [applicationCycles]
  );

  const copyFromOptions = useMemo(() => {
    const years = Array.from(new Set(nonTreeLineCycles.map((ac) => ac.year)));
    return years
      .map((year) => ({ value: year, label: year.toString() }))
      .sort((a, b) => b.value - a.value);
  }, [nonTreeLineCycles]);

  const copyToOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const existingYears = new Set(nonTreeLineCycles.map((ac) => ac.year));

    const options: { value: number; label: string }[] = [];
    for (let i = 0; i < 50; i++) {
      const year = currentYear + i;
      if (!existingYears.has(year)) {
        options.push({ value: year, label: year.toString() });
      }
    }
    return options;
  }, [nonTreeLineCycles]);

  const copyFromApplicationCycleCount = useMemo(() => {
    if (copyFromYear == null) return 0;
    return nonTreeLineCycles.filter((ac) => ac.year === copyFromYear).length;
  }, [nonTreeLineCycles, copyFromYear]);

  const handleClose = useCallback(() => {
    if (saving) {
      setAlert({
        severity: 'warning',
        message:
          'Please wait for the copy operation to complete. If this takes too long, the operation will timeout after 1 minute.',
        autoHideDuration: 10000,
      });
      return;
    }
    onClose();
  }, [onClose, saving, setAlert]);

  const handleStartCopyClick = useCallback(() => {
    if (copyFromYear == null || copyToYear == null) {
      setAlert({
        severity: 'error',
        message:
          "Please select both 'Copy From Application Cycle' and 'Copy To Application Cycle' years.",
      });
      return;
    }
    setConfirmOpen(true);
  }, [copyFromYear, copyToYear, setAlert]);

  const handleConfirmCopy = useCallback(async () => {
    if (copyFromYear == null || copyToYear == null) return;

    try {
      setConfirmOpen(false);
      setSaving(true);

      const result: CopyApplicationCyclesResponse = await copyApplicationCycles(
        copyFromYear,
        copyToYear
      );

      let message = `Successfully copied application cycles from ${copyFromYear} to ${copyToYear}.`;

      if (result?.applicationCyclesCreatedCounts) {
        const lines = Object.entries(result.applicationCyclesCreatedCounts).map(
          ([programId, count]) => `Program ID ${programId}: ${count} created`
        );
        message += `\n\nDetails:\n${lines.join('\n')}`;
      }

      setAlert({ severity: 'success', message });

      await refetchApplicationCycles();
      onClose();
    } catch (err) {
      setAlert({ severity: 'error', message: formatErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  }, [
    copyFromYear,
    copyToYear,
    copyApplicationCycles,
    refetchApplicationCycles,
    setAlert,
    onClose,
  ]);

  const canSubmit = copyFromYear != null && copyToYear != null && !saving;

  return (
    <>
      <Dialog
        open
        onClose={(_, reason) => {
          // prevent closing via backdrop click / escape while saving
          if (saving && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          handleClose();
        }}
        disableEscapeKeyDown={saving}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant='h3' color={theme.palette.grey[800]}>
            Copy Application Cycles
          </Typography>

          <IconButton
            aria-label='close'
            onClick={handleClose}
            disabled={saving}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ position: 'relative' }}>
            {saving && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Spinner size={60} type='circular' />
              </Box>
            )}

            <Stack spacing={2}>
              <SelectInput
                id='copyFromAppCycle'
                label='Copy From Application Cycle'
                value={copyFromYear?.toString() ?? ''}
                onChange={(e: any) => setCopyFromYear(Number(e.target.value))}
                selectOptions={copyFromOptions}
                disabled={saving}
                required
              />

              <SelectInput
                id='copyToAppCycle'
                label='Copy To Application Cycle'
                value={copyToYear?.toString() ?? ''}
                onChange={(e: any) => setCopyToYear(Number(e.target.value))}
                selectOptions={copyToOptions}
                disabled={saving}
                required
              />

              {copyFromYear != null && copyToYear != null && (
                <Typography variant='body2' color='text.secondary'>
                  This will create <strong>{copyFromApplicationCycleCount}</strong> application
                  cycles for <strong>{copyToYear}</strong>.
                </Typography>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant='secondary' onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleStartCopyClick} disabled={!canSubmit}>
            Start Copy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => (!saving ? setConfirmOpen(false) : undefined)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Confirm copy</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' color='text.secondary'>
            Copy application cycles from <strong>{copyFromYear}</strong> to{' '}
            <strong>{copyToYear}</strong>?
            <br />
            This will create <strong>{copyFromApplicationCycleCount}</strong> application cycles for{' '}
            <strong>{copyToYear}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='secondary' onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirmCopy} disabled={saving}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Admin_CopyApplicationCycleModal;
